/**
 * The people button — `docs/activity.md` §6.6, Board 1 A, C and Board 5.
 *
 * **One set of people for every tab**: choosing applies at once, to the
 * Calendar, the Inbox, Tasks and All activity, and is remembered for this
 * device. The quick list switches in one click — a saved group, you, the
 * people you looked at last, a branch's Unassigned; **Choose several…** opens
 * the chooser, grouped by role and place, with the people this person may not
 * see shown locked. *Save as a group* names the mix.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CALENDAR_VIEW_LABELS, firstNameOf, namesInWords, peopleLabel, toneOf,
  type ActivityGroup, type ActivityPerson, type ActivityView,
} from '@rowted/shared'
import { Popover } from '../components/overlays.js'
import { IconCheck, IconClose, IconLock, IconSearch } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { activityRequest } from './api.js'
import { useActivity } from './activity-context.js'
import { Face, FaceStack, UnassignedFace, type FacePerson } from './faces.js'
import { Glyph } from './glyphs.js'
import { PeopleSheetBody, type PeopleDraft } from './people-sheet.js'
import { byDepartment } from './departments.js'
import { BottomSheet, SheetAction } from './sheet.js'
import type { ActivityLayout } from './use-layout.js'

/* One ring, on the box, never a second inside it (the field's own focus ring). */
const SEARCH_BOX = 'flex items-center gap-2 rounded-lg border border-line px-2.5 text-[13px] text-text-faint '
  + 'focus-within:shadow-[0_0_0_2px_rgb(var(--color-accent-ring))]'
const SEARCH_INPUT = 'min-w-0 flex-1 bg-transparent text-text outline-none placeholder:text-text-faint focus-visible:shadow-none'

const VIEW_WORDS: Record<ActivityView, string> = {
  day: 'Day view', work_week: 'Work week view', week: 'Week view', month: 'Month view',
}

function SectionHead({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center px-3.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
      {children}
      <span className="flex-1" />
      {right}
    </div>
  )
}

/**
 * The button: faces, the label, ▾ (Board 1 A — 36 px, 22 px faces). On a
 * phone it is the faces alone — up to three at 24 px, 44 px tall — and opens Board 13 C's
 * sheet; on an iPad it keeps its label and opens the same as a 420 popover,
 * with To book under it, because the side panel is not there (§10.1).
 */
export function PeopleButton({ layout = 'desktop' }: { layout?: ActivityLayout }) {
  const { directory, state, people, unassigned } = useActivity()
  const compact = layout === 'phone'
  const sheet = layout !== 'desktop'
  const button = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState<'quick' | 'choose' | 'save' | 'sheet' | null>(null)
  const byId = useMemo(() => new Map((directory?.people ?? []).map((p) => [p.id, p])), [directory])
  const group = directory?.groups.find((g) => g.id === state.people.groupId) ?? null
  const shown = people.map((id) => byId.get(id)).filter((p): p is ActivityPerson => Boolean(p))
  const unassignedNames = unassigned
    .map((id) => directory?.unassigned.find((u) => u.orgUnitId === id)?.name)
    .filter((name): name is string => Boolean(name))
  const label = peopleLabel({ names: shown.map((p) => p.name), groupName: group?.name, unassigned: unassignedNames })
  /* A rep sees nobody by role — only themselves and anyone granted (Board 5 C, 300 px). */
  const rep = directory ? directory.people.every((p) => p.id === directory.me || p.seenBy === 'grant') : false

  /* The side panel's *+ Add* opens the chooser (§6.1). */
  useEffect(() => {
    const onAsk = () => setOpen('choose')
    window.addEventListener('rowted:activity-choose', onAsk)
    return () => window.removeEventListener('rowted:activity-choose', onAsk)
  }, [])

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open !== null}
        data-people-button
        onClick={() => setOpen((value) => (value ? null : sheet ? 'sheet' : 'quick'))}
        aria-label={compact ? label : undefined}
        className={cn(
          'inline-flex shrink-0 items-center rounded-[10px] border border-line bg-surface hover:bg-hover',
          /* 44 px on a phone, beside the view button and + in the page bar (Aaron, Oct 25, phone note 1). */
          compact ? 'h-11 gap-1 pl-1.5 pr-2' : 'h-9 gap-2 pl-1.5 pr-2.5',
        )}
      >
        <ButtonFaces people={shown.map(face)} unassigned={unassigned.length} compact={compact} />
        {!compact && <b className="whitespace-nowrap text-[13px]">{label}</b>}
        <svg viewBox="0 0 24 24" width={compact ? 12 : 14} height={compact ? 12 : 14} fill="none" stroke="currentColor" strokeWidth={2.2}
          strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted" aria-hidden>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open === 'sheet' && (
        <PeopleAndShow layout={layout} anchor={button.current} onClose={() => setOpen(null)} onSave={() => setOpen('save')} />
      )}
      {open === 'save' && sheet && layout === 'phone' && (
        <BottomSheet label="Save as a group" onClose={() => setOpen(null)} bodyClassName="">
          <SaveGroup onClose={() => setOpen(null)} />
        </BottomSheet>
      )}
      {open === 'quick' && (
        <Popover open onClose={() => setOpen(null)} anchor={button.current} align="right" width={rep ? 300 : 330}
          label="Whose Activity" className="rounded-xl pb-1.5 pt-1">
          <QuickList onChooseSeveral={() => setOpen('choose')} onDone={() => setOpen(null)} />
        </Popover>
      )}
      {open === 'choose' && (
        <Popover open onClose={() => setOpen(null)} anchor={button.current} align="right" width={400}
          label="Show calendars and inboxes for" className="rounded-[14px]">
          <Chooser onClose={() => setOpen(null)} onSave={() => setOpen('save')} />
        </Popover>
      )}
      {open === 'save' && layout !== 'phone' && (
        <Popover open onClose={() => setOpen(null)} anchor={button.current} align="right" width={420}
          label="Save as a group" className="rounded-[14px]">
          <SaveGroup onClose={() => setOpen(null)} />
        </Popover>
      )}
    </>
  )
}

/**
 * Board 13 C — a bottom sheet on a phone, a 420 popover on an iPad. The people
 * picked wait for **Show <n>**; a saved group applies at once.
 */
function PeopleAndShow({
  layout, anchor, onClose, onSave,
}: { layout: ActivityLayout; anchor: HTMLElement | null; onClose: () => void; onSave: () => void }) {
  const { people, unassigned, change, me, state, directory } = useActivity()
  const [draft, setDraft] = useState<PeopleDraft>({ picked: new Set(people), branches: new Set(unassigned) })
  const count = draft.picked.size + draft.branches.size
  const apply = () => {
    const ids = [...draft.picked]
    const branches = [...draft.branches]
    change({
      people: { ...groupKept(state.people.groupId, directory?.groups ?? [], ids),
        ids: ids.length > 0 || branches.length > 0 ? ids : [me.user.id], unassigned: branches },
      recentPeople: [...ids.filter((id) => id !== me.user.id), ...state.recentPeople.filter((id) => !ids.includes(id))]
        .slice(0, 8),
    })
  }
  const onGroup = (group: { id: string | null; memberIds: string[] }) => {
    change(group.id
      ? { people: { groupId: group.id, ids: group.memberIds, unassigned: [] } }
      : { people: { ids: group.memberIds, unassigned: [] } })
    onClose()
  }
  const footer = (
    <>
      <SheetAction onClick={() => { apply(); onSave() }} disabled={draft.picked.size < 2}>
        <Glyph name="bookmark" size={16} />Save as a group
      </SheetAction>
      <SheetAction primary onClick={() => { apply(); onClose() }} disabled={count === 0}>
        <Glyph name="check" size={16} />Show {count}
      </SheetAction>
    </>
  )
  const body = <PeopleSheetBody draft={draft} setDraft={setDraft} onGroup={onGroup} showToBook={layout !== 'phone'} />
  if (layout === 'phone') {
    return (
      <BottomSheet fill title="People and what to show" sub="remembered on this phone" label="People and what to show"
        onClose={onClose} footer={footer}>
        {body}
      </BottomSheet>
    )
  }
  return (
    <Popover open onClose={onClose} anchor={anchor} align="right" width={420} label="People and what to show"
      className="rounded-[14px]">
      <div className="flex max-h-[min(760px,80dvh)] flex-col">
        <div className="flex items-center gap-3 border-b border-line-faint px-4 pb-3 pt-3.5">
          <div className="flex flex-col">
            <b className="text-[15.5px]">People and what to show</b>
            <span className="text-[12px] text-text-muted">remembered on this iPad</span>
          </div>
          <span className="flex-1" />
          <button type="button" aria-label="Close" onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line text-text-muted">
            <Glyph name="close" size={15} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3.5 pt-1">{body}</div>
        <div className="flex gap-2.5 border-t border-line px-4 py-3">{footer}</div>
      </div>
    </Popover>
  )
}

/**
 * A saved group stays named while its own people are the ones picked, so
 * adding a branch's Unassigned reads *Sales — Harrisburg + Unassigned*
 * (§6.6, Board 19).
 */
function groupKept(groupId: string | undefined, groups: readonly ActivityGroup[], ids: readonly string[]): { groupId?: string } {
  const group = groups.find((one) => one.id === groupId)
  return group && group.memberIds.length === ids.length && group.memberIds.every((id) => ids.includes(id))
    ? { groupId: group.id } : {}
}

/** Applies a choice of people, at once, to every tab (§6.6). */
function useChoose() {
  const { change, state, me, directory } = useActivity()
  return {
    person: (id: string) => change({
      people: { ids: [id], unassigned: [] },
      recentPeople: [id, ...state.recentPeople.filter((other) => other !== id)].slice(0, 8),
    }),
    group: (group: ActivityGroup) => change({
      people: { groupId: group.id, ids: group.memberIds, unassigned: [] },
      calendar: { viewSeveral: group.opensIn },
    }),
    unassigned: (orgUnitId: string) => change({ people: { ids: [], unassigned: [orgUnitId] } }),
    several: (ids: string[], branches: string[]) => change({
      people: { ...groupKept(state.people.groupId, directory?.groups ?? [], ids),
        ids: ids.length > 0 || branches.length > 0 ? ids : [me.user.id], unassigned: branches },
      recentPeople: [...ids.filter((id) => id !== me.user.id), ...state.recentPeople.filter((id) => !ids.includes(id))]
        .slice(0, 8),
    }),
  }
}

function Row({
  selected, onClick, lead, title, sub, right,
}: {
  selected?: boolean; onClick: () => void; lead: React.ReactNode; title: string; sub: string
  right?: React.ReactNode
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn('mx-1 flex w-[calc(100%-8px)] items-center gap-2.5 rounded-[9px] px-3 py-[7px] text-left',
        selected ? 'bg-green-band-bg' : 'hover:bg-hover')}>
      {lead}
      <span className="flex min-w-0 flex-1 flex-col">
        <b className={cn('truncate text-[13px] font-semibold', selected ? 'text-accent-dark' : 'text-text')}>{title}</b>
        <span className="truncate text-[11.5px] text-text-muted">{sub}</span>
      </span>
      {right}
    </button>
  )
}

const face = (person: ActivityPerson): FacePerson =>
  ({ id: person.id, name: person.name, initials: person.initials, colour: person.colour })

/** A department's own line inside *One person*: *Sales · Harrisburg* and how many. */
function DepartmentHead({ name, count }: { name: string; count: number }) {
  return (
    <div className="flex items-center gap-2 px-3.5 pb-0.5 pt-2 text-[11.5px] font-semibold text-text-muted">
      <span className="truncate">{name}</span>
      <span className="flex-1" />
      <span className="tnum font-medium text-text-faint">{count}</span>
    </div>
  )
}

/**
 * The quick list (Board 1 C, 330 px) — with everyone under *One person*
 * (Aaron, Oct 23: "It should show everyone I have access to grouped by
 * department"). It was you and the three people looked at last, so an owner
 * whose team had never been opened saw only themselves and Unassigned until
 * *Choose several…*. A name or a department searches it; one press shows
 * that one person, on every tab.
 */
function QuickList({ onChooseSeveral, onDone }: { onChooseSeveral: () => void; onDone: () => void }) {
  const { directory, state, me, people, unassigned } = useActivity()
  const choose = useChoose()
  const [q, setQ] = useState('')
  if (!directory) return <div className="px-4 py-3 text-[13px] text-text-muted">Loading…</div>
  const query = q.trim().toLowerCase()
  const byId = new Map(directory.people.map((p) => [p.id, p]))
  const mine = byId.get(me.user.id)
  const others = directory.people.filter((p) => p.id !== me.user.id)
  /* A rep's list is Board 5 C: their calendars and the sentence, nothing to choose several from. */
  if (others.every((p) => p.seenBy === 'grant')) return <RepList onDone={onDone} />
  const department = (p: ActivityPerson) => [p.role, p.place].filter(Boolean).join(' · ')
  const matches = (p: ActivityPerson) => !query || p.name.toLowerCase().includes(query)
    || department(p).toLowerCase().includes(query)
  const departments = byDepartment(others.filter(matches), people)
  const groups = directory.groups.filter((g) => !query || g.name.toLowerCase().includes(query))
  const units = directory.unassigned.filter((u) => !query || `unassigned ${u.name}`.toLowerCase().includes(query))
  const done = (run: () => void) => { run(); onDone() }
  const single = !state.people.groupId && unassigned.length === 0 && people.length === 1 ? people[0] : null
  const youShown = mine && matches(mine)
  const personRow = (person: ActivityPerson, sub: string) => (
    <Row key={person.id} selected={single === person.id} onClick={() => done(() => choose.person(person.id))}
      lead={<Face person={face(person)} size={26} />}
      title={person.name}
      sub={sub}
      right={single === person.id ? <IconCheck size={16} className="text-accent" /> : undefined} />
  )

  return (
    /* The boards' lists read at `line-height: normal` (Board 1 C is 479 tall, not 538). */
    <div className="flex max-h-[min(640px,78dvh)] flex-col leading-[normal]">
      <div className="px-2.5 py-2">
        <label className={SEARCH_BOX + ' h-[34px]'}>
          <IconSearch size={15} />
          <input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Find a person or group"
            className={SEARCH_INPUT} autoFocus />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" data-quick-list>
        {groups.length > 0 && (
          <>
            <SectionHead>Saved</SectionHead>
            {groups.map((group) => {
              const members = group.memberIds.map((id) => byId.get(id)).filter((p): p is ActivityPerson => Boolean(p))
              const on = state.people.groupId === group.id
              return (
                <Row key={group.id} selected={on} onClick={() => done(() => choose.group(group))}
                  lead={<FaceStack people={members.map(face)} size={20} />}
                  title={group.name}
                  sub={`${members.length} people · ${VIEW_WORDS[group.opensIn]}`}
                  right={on ? <IconCheck size={16} className="text-accent" /> : undefined} />
              )
            })}
          </>
        )}
        {(youShown || departments.length > 0) && (
          <>
            <SectionHead>One person</SectionHead>
            {youShown && mine && personRow(mine, 'you')}
            {departments.map(([name, list]) => (
              <div key={name} data-department={name}>
                <DepartmentHead name={name} count={list.length} />
                {list.map((person) => personRow(person,
                  [person.workDays, person.seenBy === 'grant' ? 'you can see' : null].filter(Boolean).join(' · ')
                    || department(person)))}
              </div>
            ))}
          </>
        )}
        {units.length > 0 && (
          <>
            <SectionHead>Nobody’s yet</SectionHead>
            {units.map((unit) => (
              <Row key={unit.orgUnitId} selected={unassigned.length === 1 && unassigned[0] === unit.orgUnitId && people.length === 0}
                onClick={() => done(() => choose.unassigned(unit.orgUnitId))}
                lead={<UnassignedFace size={26} />}
                title={`Unassigned · ${unit.name}`}
                sub="texts, emails and tasks nobody owns"
                right={unit.count > 0 ? (
                  <span className="tnum rounded-full bg-line-soft px-[7px] py-px text-[11px] font-semibold text-text-muted">
                    {unit.count}
                  </span>
                ) : undefined} />
            ))}
          </>
        )}
        {query && groups.length === 0 && !youShown && departments.length === 0 && units.length === 0 && (
          <div className="px-3.5 py-3 text-[12.5px] text-text-muted">Nobody you can see is called that.</div>
        )}
      </div>
      <div className="mt-1 flex items-center border-t border-line-faint px-3.5 pb-1 pt-2.5">
        <button type="button" onClick={onChooseSeveral}
          className="flex items-center gap-1.5 text-[12.5px] font-semibold text-accent">
          <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
            <circle cx="17" cy="9" r="2.5" /><path d="M17 14.5c2.4 0 4 1.6 4 4" />
          </svg>
          Choose several…
        </button>
        <span className="flex-1" />
        <span className="text-[11.5px] text-text-faint">applies to every tab</span>
      </div>
    </div>
  )
}

/**
 * The button's faces (§3, Board 19 A): the people and then each Unassigned as
 * its dashed face, up to four (three on a phone), then *+n* — *TR HB LO MP +1
 * Sales — Harrisburg + Unassigned*.
 */
function ButtonFaces({ people, unassigned, compact }: { people: FacePerson[]; unassigned: number; compact: boolean }) {
  const size = compact ? 24 : 22
  const max = compact ? 3 : 4
  const marks: (FacePerson | null)[] = [...people, ...Array.from({ length: unassigned }, () => null)]
  if (marks.length === 0) return null
  return (
    <span className="inline-flex items-center">
      {marks.slice(0, max).map((person, index) => (
        <span key={person?.id ?? `unassigned-${index}`} className="inline-flex rounded-full border-2 border-white bg-white"
          style={{ marginLeft: index === 0 ? 0 : -(compact ? 7 : 6) }}>
          {person ? <Face person={person} size={size} /> : <UnassignedFace size={size} />}
        </span>
      ))}
      {marks.length > max && (
        <span className="ml-1 text-[11.5px] font-semibold text-text-muted">+{marks.length - max}</span>
      )}
    </span>
  )
}

/**
 * Where a locked person is placed (§6.6): *Carlisle branch*, *West district* —
 * the kind is added only when the unit's own name does not already say it.
 */
function placeWords(person: { place?: string | null; placeKind?: string | null }): string {
  const place = person.place ?? ''
  const kind = person.placeKind === 'branch' || person.placeKind === 'district' || person.placeKind === 'region'
    ? person.placeKind : null
  if (!kind || place.toLowerCase().endsWith(` ${kind}`) || place.toLowerCase() === kind) return place
  return [place, kind].filter(Boolean).join(' ')
}

/**
 * Board 5 C — a rep's list: *Calendars* with their own and anyone granted (a
 * tick shows them too), and what they may see in a sentence. There is no one
 * else to choose, so *Choose several…* is absent (§6.6).
 */
function RepList({ onDone }: { onDone: () => void }) {
  const { directory, me, people, state, change, unassigned } = useActivity()
  const choose = useChoose()
  const mine = directory?.people.find((p) => p.id === me.user.id)
  const granted = (directory?.people ?? []).filter((p) => p.id !== me.user.id && p.seenBy === 'grant')
  const rows = [...(mine ? [mine] : []), ...granted]
  const units = directory?.unassigned ?? []
  const toggle = (id: string) => {
    /* From Unassigned on its own, a tick goes back to people (§19 B12). */
    if (people.length === 0) {
      change({ people: { ids: [id], unassigned: [] } })
      return
    }
    const ids = people.includes(id) ? people.filter((other) => other !== id) : [...people, id]
    change({ people: { ids: ids.length > 0 ? ids : [me.user.id], unassigned: state.people.unassigned } })
  }
  return (
    <div className="px-4 pb-2.5 pt-2.5 leading-[normal]">
      <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">Calendars</div>
      {rows.map((person) => {
        const on = people.includes(person.id)
        const edge = toneOf(person.colour).edge
        return (
          <button key={person.id} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(person.id)}
            className="flex h-8 w-full items-center gap-2 text-left text-[12.5px]">
            <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] text-white"
              style={{ borderColor: edge, background: on ? edge : 'rgb(var(--color-surface))' }}>
              {on && <IconCheck size={11} />}
            </span>
            <Face person={face(person)} size={22} />
            <span className="flex min-w-0 flex-col">
              <span className={cn('truncate text-text', on ? 'font-semibold' : 'font-medium')}>
                {person.name}{person.id === me.user.id ? ' (you)' : ''}
              </span>
              {person.seenBy === 'grant' && <span className="text-[10.5px] text-text-faint">you can see</span>}
            </span>
          </button>
        )
      })}
      {/*
        * A salesperson placed in the branch sees its Unassigned (§5.1, §19
        * B12), and picks it here like a person (Board 8 B).
        */}
      {units.length > 0 && (
        <>
          <div className="mb-1 mt-2.5 text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">Nobody’s yet</div>
          <div className="-mx-4">
            {units.map((unit) => (
            <Row key={unit.orgUnitId} selected={unassigned.includes(unit.orgUnitId) && people.length === 0}
              onClick={() => { choose.unassigned(unit.orgUnitId); onDone() }}
              lead={<UnassignedFace size={26} />}
              title={`Unassigned · ${unit.name}`}
              sub="texts, emails and tasks nobody owns"
              right={unit.count > 0 ? (
                <span className="tnum rounded-full bg-line-soft px-[7px] py-px text-[11px] font-semibold text-text-muted">
                  {unit.count}
                </span>
              ) : undefined} />
            ))}
          </div>
        </>
      )}
      <RepNote names={granted.map((p) => p.name)} />
    </div>
  )
}

/** Board 5 C's sentence. */
function RepNote({ names }: { names: string[] }) {
  const grants = names.map((name) => `${name}’s calendar`)
  return (
    <div className="mt-2.5 rounded-[10px] bg-surface-alt px-3 py-2.5 text-[12px] leading-[1.45] text-text-muted">
      You can see your own calendar and inbox{grants.length > 0 ? `, and ${namesInWords(grants)}` : ''}.
      {' '}An admin can give you more.
    </div>
  )
}

/** A 18 px box in the person's colour (Board 5 A). */
function PersonBox({ colour, on }: { colour: string | null; on: boolean }) {
  const edge = toneOf(colour).edge
  return (
    <span aria-hidden className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border-[1.5px] text-white"
      style={{ background: on ? edge : 'rgb(var(--color-surface))', borderColor: edge }}>
      {on && <IconCheck size={12} />}
    </span>
  )
}

/** Choose several (Board 5 A, 400 px). */
function Chooser({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const { directory, me, people, unassigned, state, change } = useActivity()
  const choose = useChoose()
  const [picked, setPicked] = useState<Set<string>>(new Set(people))
  const [branches, setBranches] = useState<Set<string>>(new Set(unassigned))
  const [q, setQ] = useState('')
  if (!directory) return null
  const query = q.trim().toLowerCase()
  const match = (name: string) => !query || name.toLowerCase().includes(query)

  /*
   * A section per role and place, ordered by size, the people shown now first
   * (Board 5 A: Tom, Hannah, Luis, Maya, then Sara) — `byDepartment`, the
   * quick list's own grouping; you on your own; then Unassigned; then the locked.
   */
  const ordered = byDepartment(directory.people.filter((person) => person.id !== me.user.id && match(person.name)), people)
  /* Unassigned answers the search like a person (*sa* leaves Sara alone). */
  const units = directory.unassigned.filter((unit) => match(`Unassigned · ${unit.name}`))
  /* The locked, by where they are placed (Board 5 A: Carlisle branch, then West district). */
  const locked = directory.locked.filter((p) => match(p.name))
    .sort((a, b) => (a.place ?? '').localeCompare(b.place ?? '') || a.name.localeCompare(b.name))
  const you = directory.people.find((p) => p.id === me.user.id)
  const toggle = (id: string) => setPicked((set) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const count = picked.size + branches.size

  return (
    <div className="flex max-h-[min(825px,85dvh)] flex-col leading-[normal]">
      <div className="flex items-center gap-2.5 border-b border-line-faint px-4 py-3.5">
        <b className="text-[14.5px]">Show calendars and inboxes for</b>
        <span className="flex-1" />
        <button type="button" aria-label="Close" onClick={onClose} className="text-text-muted hover:text-text">
          <IconClose size={18} />
        </button>
      </div>
      <div className="px-3.5 pb-1 pt-2.5">
        <label className={SEARCH_BOX + ' h-9'}>
          <IconSearch size={15} />
          <input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Find a person"
            className={SEARCH_INPUT} />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-1">
        {ordered.map(([key, list]) => (
          <div key={key}>
            <SectionHead right={(
              <button type="button" className="text-[12px] normal-case tracking-normal text-accent"
                onClick={() => setPicked((set) => new Set([...set, ...list.map((p) => p.id)]))}>All</button>
            )}>{key}</SectionHead>
            {list.map((person) => (
              <ChooserRow key={person.id} person={person} on={picked.has(person.id)} onToggle={() => toggle(person.id)} />
            ))}
          </div>
        ))}
        {you && match(you.name) && (
          <>
            <SectionHead>You</SectionHead>
            <ChooserRow person={you} on={picked.has(you.id)} onToggle={() => toggle(you.id)} sub={you.role} />
          </>
        )}
        {units.length > 0 && (
          <>
            <SectionHead>Nobody’s yet</SectionHead>
            {units.map((unit) => (
              <button key={unit.orgUnitId} type="button" onClick={() => setBranches((set) => {
                const next = new Set(set)
                if (next.has(unit.orgUnitId)) next.delete(unit.orgUnitId)
                else next.add(unit.orgUnitId)
                return next
              })} className="flex w-full items-center gap-2.5 px-3.5 py-[7px] text-left hover:bg-hover">
                <PersonBox colour={null} on={branches.has(unit.orgUnitId)} />
                <UnassignedFace size={26} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <b className="text-[13px] font-semibold">Unassigned · {unit.name}</b>
                  <span className="text-[11.5px] text-text-muted">texts, emails and tasks nobody owns · no calendar</span>
                </span>
              </button>
            ))}
          </>
        )}
        {locked.length > 0 && (
          <>
            <SectionHead>Others</SectionHead>
            {locked.map((person) => (
              <div key={person.id} className="flex items-center gap-2.5 px-3.5 py-[7px] text-text-faint">
                <span className="inline-flex w-[18px] justify-center"><IconLock size={14} /></span>
                <Face person={{ ...person, colour: person.colour ?? null }} size={26} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <b className="text-[13px] font-semibold text-text-faint">{person.name}</b>
                  <span className="text-[11.5px]">{placeWords(person)} — ask an admin to see this calendar</span>
                </span>
              </div>
            ))}
          </>
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-line bg-surface-alt px-3.5 py-3">
        <button type="button" className="h-8 rounded-lg px-2 text-[12.5px] font-semibold text-text-muted hover:text-text"
          onClick={() => { choose.person(me.user.id); onClose() }}>Just me</button>
        <span className="flex-1" />
        <button type="button" disabled={picked.size < 2}
          onClick={() => {
            /* The picks go to the form; a group they still match stays picked if the form is cancelled. */
            change({ people: { ...groupKept(state.people.groupId, directory.groups, [...picked]),
              ids: [...picked], unassigned: [...branches] } })
            onSave()
          }}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-surface px-3.5 text-[12.5px] font-semibold disabled:opacity-50">
          <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 3h12v18l-6-4-6 4z" /></svg>
          Save as a group
        </button>
        <button type="button" disabled={count === 0}
          onClick={() => { choose.several([...picked], [...branches]); void state; onClose() }}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-50">
          <IconCheck size={14} />
          Show {count} {count === 1 ? 'person' : 'people'}
        </button>
      </div>
    </div>
  )
}

function ChooserRow({ person, on, onToggle, sub }: {
  person: ActivityPerson; on: boolean; onToggle: () => void; sub?: string
}) {
  return (
    <button type="button" role="checkbox" aria-checked={on} onClick={onToggle}
      className="flex w-full items-center gap-2.5 px-3.5 py-[7px] text-left hover:bg-hover">
      <PersonBox colour={person.colour} on={on} />
      <Face person={face(person)} size={26} />
      <span className="flex min-w-0 flex-1 flex-col">
        <b className="truncate text-[13px] font-semibold">{person.name}</b>
        <span className="truncate text-[11.5px] text-text-muted">
          {sub ?? [person.role, person.workDays].filter(Boolean).join(' · ')}
          {person.seenBy === 'grant' ? ' · you can see' : ''}
        </span>
      </span>
    </button>
  )
}

/** Save as a group (Board 5 B, 420 px). */
export function SaveGroup({ onClose }: { onClose: () => void }) {
  const { directory, people, change, reloadDirectory } = useActivity()
  const byId = new Map((directory?.people ?? []).map((p) => [p.id, p]))
  const members = people.map((id) => byId.get(id)).filter((p): p is ActivityPerson => Boolean(p))
  const [name, setName] = useState(namesInWords(members.map((p) => firstNameOf(p.name))))
  const [opensIn, setOpensIn] = useState<ActivityView>('day')
  const [shared, setShared] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const made = await activityRequest<{ id: string }>('/activity/groups', {
        method: 'POST', body: { name, memberIds: members.map((p) => p.id), opensIn, shared },
      })
      change({ people: { groupId: made.id, ids: members.map((p) => p.id), unassigned: [] }, calendar: { viewSeveral: opensIn } })
      reloadDirectory()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  /* Board 5 B, measure for measure, at the boards' `line-height: normal`. */
  return (
    <div className="flex flex-col leading-[normal]">
      <div className="flex items-center gap-3 border-b border-line-faint px-4 py-3.5">
        <span className="flex flex-col">
          <b className="text-[14.5px]">Save as a group</b>
          <span className="text-[12px] text-text-muted">pick it from the people button on every tab</span>
        </span>
        <span className="flex-1" />
        <button type="button" aria-label="Close" onClick={onClose}
          className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full text-text-muted hover:bg-hover hover:text-text">
          <IconClose size={18} />
        </button>
      </div>
      <div className="flex flex-col px-4 py-3.5">
        <label className="flex flex-col">
          <span className="mb-[5px] text-[11.5px] font-medium text-text-muted">Name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} autoFocus
            className="h-9 rounded-lg border border-accent bg-surface px-[11px] text-[13px] shadow-[0_0_0_3px_rgb(var(--color-accent-ring))] outline-none" />
        </label>
        <div className="mt-3 flex items-center gap-2.5">
          <FaceStack people={members.map(face)} size={26} />
          <span className="text-[12.5px] text-text-body">{namesInWords(members.map((p) => firstNameOf(p.name)))}</span>
        </div>
        <div className="mt-3.5 flex flex-col">
          <span className="mb-[5px] text-[11.5px] font-medium text-text-muted">Opens in</span>
          <span className="inline-flex w-fit rounded-[10px] bg-line-soft p-[3px]">
            {(['day', 'work_week', 'week', 'month'] as const).map((view) => (
              <button key={view} type="button" role="radio" aria-checked={opensIn === view} onClick={() => setOpensIn(view)}
                className={cn('inline-flex h-[26px] items-center whitespace-nowrap rounded-lg px-3 text-[12.5px] font-semibold',
                  opensIn === view ? 'bg-surface text-text shadow-[0_1px_2px_rgba(20,20,10,.12)]' : 'text-text-muted')}>
                {CALENDAR_VIEW_LABELS[view]}
              </button>
            ))}
          </span>
        </div>
        <button type="button" role="switch" aria-checked={shared} onClick={() => setShared((v) => !v)}
          className="mt-3.5 flex items-start gap-2.5 text-left">
          <span className={cn('inline-flex h-[19px] w-8 shrink-0 items-center rounded-full p-0.5 transition',
            shared ? 'bg-accent' : 'bg-line-strong')}>
            <span className={cn('h-[15px] w-[15px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.25)] transition',
              shared && 'translate-x-[13px]')} />
          </span>
          <span className="flex flex-col">
            <b className="text-[13px] font-bold">Share with the branch’s admins</b>
            <span className="text-[11.5px] text-text-muted">
              they see it under Saved; only people they’re allowed to see are shown
            </span>
          </span>
        </button>
        {error && <span className="mt-3 text-[12px] text-danger">{error}</span>}
      </div>
      <div className="flex items-center justify-end gap-2.5 border-t border-line bg-surface-alt px-4 py-3">
        <button type="button" onClick={onClose}
          className="inline-flex h-[34px] items-center rounded-lg border border-line bg-surface px-3.5 text-[12.5px] font-semibold">Cancel</button>
        <button type="button" disabled={busy || !name.trim()} onClick={() => void save()}
          className="inline-flex h-[34px] items-center gap-1.5 rounded-lg border border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-50">
          <IconCheck size={14} />Save group
        </button>
      </div>
    </div>
  )
}
